import dayjs from 'dayjs';
import type { Spending, SpendingDetail } from '../../../api/financials-client/financials-client.type';
import type { Project } from '../../../api/projects-client/projects-client.type';

/**
 * Get the available spending years
 *
 * @param spendingData - The spending data
 * @returns The available spending years
 */
function getAvailableSpendingYears(spendingData: Spending[] | undefined): Set<number> {
  if (!spendingData || spendingData.length === 0) {
    return new Set([dayjs().year()]);
  }

  return new Set(spendingData.map((item) => item.year));
}

/**
 * Filters projects and their subprojects based on a search term
 *
 * @param projects - Array of projects to filter
 * @param searchTerm - Search term to filter by
 * @returns Filtered array of projects with matching subprojects
 */
function filterProjectsBySearch(projects: Project[], searchTerm: string): Project[] {
  if (!searchTerm) return projects;

  const searchTermLower = searchTerm.toLowerCase();

  return projects
    .map((project) => {
      const projectMatches = project.name.toLowerCase().includes(searchTermLower);
      const matchingSubprojects = project.subprojects.filter((subproject) =>
        subproject.name.toLowerCase().includes(searchTermLower),
      );

      // If project matches or has matching subprojects, include it with filtered subprojects
      if (projectMatches || matchingSubprojects.length > 0) {
        return {
          ...project,
          subprojects: projectMatches ? project.subprojects : matchingSubprojects,
        };
      }
      return null;
    })
    .filter((project): project is Project => project !== null);
}

/**
 * Get the total spending for a given year
 *
 * @param spendingData - The spending data
 * @param selectedYear - The year to get the total spending for
 * @returns The total spending for the given year
 */
function getTotalSpending(spendingData: Spending[] | undefined, selectedYear: number | null): number | null {
  if (!spendingData || spendingData.length === 0 || !selectedYear) {
    return null;
  }

  return spendingData
    .filter((entry) => entry.year === selectedYear && !!entry.subproject_id)
    .reduce((acc, entry) => acc + (entry.budget || 0), 0);
}

/**
 * Rollup subproject spending for a given year
 *
 * @param spendingData - The spending data
 * @param selectedYear - The year to rollup the spending for
 * @returns The rolled up spending data as a record of project ids with a list of monthly spending amounts as values
 *
 * example output:
 * {
 *  "project_id_1": [100, 200, 300],
 *  "project_id_2": [400, 500, 600],
 * }
 */
function rollupSubprojectSpending(
  spendingData: Spending[] | undefined,
  selectedYear: number | null,
): Record<string, number[]> | null {
  if (!spendingData || spendingData.length === 0 || !selectedYear) {
    return null;
  }

  const distinctProjects = new Set(
    spendingData.filter((entry) => entry.year === selectedYear).map((entry) => entry.project_id),
  );

  const perProjectSpendingRollup: Record<string, number[]> = {};
  Array.from(distinctProjects).map((projectId) => {
    const projectSpending = spendingData.filter(
      (entry) => entry.year === selectedYear && entry.project_id === projectId && entry.budget !== null,
    );
    const monthsWithBudget = new Set(projectSpending.map((entry) => entry.month));

    const rollup = [] as number[];
    Array.from(monthsWithBudget).map((month) => {
      const monthSpending = projectSpending.filter((entry) => entry.month === month);
      rollup.push(monthSpending.reduce((acc, entry) => acc + (entry.budget || 0), 0));
    });

    perProjectSpendingRollup[projectId] = rollup;
  });

  return perProjectSpendingRollup;
}

/**
 * Get the average monthly spending per project for a given year, then average the results
 *
 * @param spendingData - The spending data
 * @param selectedYear - The year to get the average monthly spending for
 * @returns The average monthly spending per project for the given year
 */
function getAverageMonthlySpendingPerProject(
  spendingData: Spending[] | undefined,
  selectedYear: number | null,
): number | null {
  const perProjectSpendingRollup = rollupSubprojectSpending(spendingData, selectedYear);

  if (!perProjectSpendingRollup) {
    return null;
  }

  const perProjectSpendingAverages = Object.fromEntries(
    Object.entries(perProjectSpendingRollup).map(([projectId, rollup]) => {
      return rollup.length
        ? [projectId, rollup.reduce((acc, entry) => acc + entry, 0) / rollup.length]
        : [projectId, 0];
    }),
  );

  return Object.keys(perProjectSpendingAverages).length
    ? Object.values(perProjectSpendingAverages).reduce((acc, entry) => acc + entry, 0) /
        Object.keys(perProjectSpendingAverages).length
    : null;
}

/**
 * Get the median monthly spending per project for a given year, then calculate the median of the results
 *
 * @param spendingData - The spending data
 * @param selectedYear - The year to get the median monthly spending for
 * @returns The median monthly spending per project for the given year
 */
function getMedianMonthlySpendingPerProject(
  spendingData: Spending[] | undefined,
  selectedYear: number | null,
): number | null {
  const perProjectSpendingRollup = rollupSubprojectSpending(spendingData, selectedYear);

  if (!perProjectSpendingRollup) {
    return null;
  }

  const perProjectSpendingMedians = Object.fromEntries(
    Object.entries(perProjectSpendingRollup).map(([projectId, rollup]) => {
      const sortedRollup = rollup.sort((a, b) => a - b);
      return [
        projectId,
        rollup.length % 2 === 0
          ? (sortedRollup[rollup.length / 2 - 1] + sortedRollup[rollup.length / 2]) / 2
          : sortedRollup[Math.floor(rollup.length / 2)],
      ];
    }),
  );

  const sortedMedians = Object.values(perProjectSpendingMedians).sort((a, b) => a - b);

  return sortedMedians.length % 2 === 0
    ? (sortedMedians[sortedMedians.length / 2 - 1] + sortedMedians[sortedMedians.length / 2]) / 2
    : sortedMedians[Math.floor(sortedMedians.length / 2)];
}

/**
 * Get the minimum monthly spend for a given year
 *
 * @param spendingData - The spending data
 * @param selectedYear - The year to get the minimum monthly spend for
 * @returns The minimum monthly spend for the given year
 */
function getMinimumMonthlySpend(spendingData: Spending[] | undefined, selectedYear: number | null): number | null {
  const perProjectSpendingRollup = rollupSubprojectSpending(spendingData, selectedYear);

  if (!perProjectSpendingRollup) {
    return null;
  }

  const sortedMinValues = Object.values(perProjectSpendingRollup).map((rollup) => Math.min(...rollup));
  return sortedMinValues.length ? Math.min(...sortedMinValues) : null;
}

/**
 * Get the maximum monthly spend for a given year
 *
 * @param spendingData - The spending data
 * @param selectedYear - The year to get the maximum monthly spend for
 * @returns The maximum monthly spend for the given year
 */
function getMaximumMonthlySpend(spendingData: Spending[] | undefined, selectedYear: number | null): number | null {
  const perProjectSpendingRollup = rollupSubprojectSpending(spendingData, selectedYear);

  if (!perProjectSpendingRollup) {
    return null;
  }

  const sortedMaxValues = Object.values(perProjectSpendingRollup).map((rollup) => Math.max(...rollup));
  return sortedMaxValues.length ? Math.max(...sortedMaxValues) : null;
}

/**
 * Transform the spending data into the required format
 *
 * @param spendingData - The spending data
 * @param selectedYear - The year to transform the spending data for
 * @returns The transformed spending data
 */
function buildSpendingPayload(
  spendingData: Spending[] | undefined,
  selectedYear: number | null,
): { portfolio_id: string; year: number; projects: Record<string, Record<string, SpendingDetail[]>> } | null {
  if (!spendingData || spendingData.length === 0 || !selectedYear) {
    return null;
  }

  const spendingDataToSave = spendingData.filter((entry) => entry.year === selectedYear);

  // Transform the data into the required format
  const payload = {
    portfolio_id: spendingDataToSave[0].portfolio_id,
    year: selectedYear,
    projects: spendingDataToSave.reduce(
      (acc, entry) => {
        const projectId = entry.project_id;
        const subprojectId = entry.subproject_id || 'self';

        if (!acc[projectId]) {
          acc[projectId] = {};
        }

        if (!acc[projectId][subprojectId]) {
          acc[projectId][subprojectId] = [];
        }

        acc[projectId][subprojectId].push({
          month: entry.month,
          budget: entry.budget ?? 0,
        });

        if (!acc[projectId]['self']) {
          acc[projectId]['self'] = [];
        }

        const selfBudgetForMonth = acc[projectId]['self'].find((item) => item.month === entry.month);

        if (selfBudgetForMonth && selfBudgetForMonth.budget) {
          selfBudgetForMonth.budget += entry.budget ?? 0;
        } else {
          acc[projectId]['self'].push({
            month: entry.month,
            budget: entry.budget ?? 0,
          });
        }
        return acc;
      },
      {} as Record<string, Record<string, SpendingDetail[]>>,
    ),
  };

  return payload;
}

export {
  buildSpendingPayload,
  filterProjectsBySearch,
  getAvailableSpendingYears,
  getAverageMonthlySpendingPerProject,
  getMaximumMonthlySpend,
  getMedianMonthlySpendingPerProject,
  getMinimumMonthlySpend,
  getTotalSpending,
};
